//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { initiateRightsRequest } from '@apis/rights-management/rightsManagementApi';
import { InitiateRightsRequestRequest } from '@apis/rights-management/rightsManagementApiModels';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import PartialCuid from '@models/PartialCuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';

export interface InitializeRightsMutationParams {
  cuid: PartialCuid;
  request: InitiateRightsRequestRequest;
}

export const useInitiateRightsMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cuid, request }: InitializeRightsMutationParams) =>
      initiateRightsRequest(cuid, request),
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Initiate rights request',
          message: `Unable to initiate rights request. Please try again or contact Bazaarvoice Support.`,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.UniversalImageActions,
          QueryKeyConstants.UniversalImageActionsMetadata,
        ],
      });
    },
  });
};
